import {Link} from 'react-router'

import {Icon} from '@ui/Icon/icon'
import {PackIndex} from '@core/packs/PackIndex'

import {homeStructuredData, type EndpaperContent, type EndpaperStyles, type GalleryTile, type HouseHome} from './doorstep'

/**
 * The doorstep, rendered after the house's `<Wall/>`: the JSON-LD blocks, then the endpaper. A
 * crawler reads the SSR document past the wall's first page; a human meets the endpaper only at the
 * wall's end. Signed out only — a signed-in document carries neither.
 *
 * `gallery` is the first page the document server-renders; pass it only where that page is the
 * house's canonical home (an unsearched `/`).
 */
export function HomeDoorstep({house, anon, gallery = []}: {house: HouseHome; anon: boolean; gallery?: readonly GalleryTile[]}) {
    if (!anon) return null
    return (
        <>
            {homeStructuredData(house, gallery).map((block) => (
                <script
                    key={String(block['@type'])}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{__html: JSON.stringify(block)}}
                />
            ))}
            <Endpaper content={house.endpaper} styles={house.styles} />
        </>
    )
}

function Endpaper({content, styles}: {content: EndpaperContent; styles: EndpaperStyles}) {
    const {intro, catalog, sections, faq, packIndex, coda} = content
    return (
        <div className={styles.endpaper}>
            <section className={styles.intro}>
                <h1 className={styles.h1}>{intro.h1}</h1>
                <p className={styles.lede}>{intro.lede}</p>
                {intro.stats && <p className={styles.stats}>{intro.stats}</p>}
            </section>

            {catalog && (
                <section className={styles.catalog}>
                    <div className={styles.catalogHeader}>
                        <h2 className={styles.h2}>{catalog.h2}</h2>
                        {catalog.headerLinks && (
                            <div className={styles.headerLinks}>
                                {catalog.headerLinks.map((l) => (
                                    <Link key={`${l.href}-${l.label}`} to={l.href} className={styles.headerLink} prefetch="intent">
                                        {l.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                    <ul className={styles.catalogList}>
                        {catalog.links.map((l) => (
                            <li key={l.href}>
                                <Link to={l.href} className={styles.catalogLink}>
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    {catalog.seeAll && (
                        <Link to={catalog.seeAll.href} className={styles.seeAll} prefetch="intent">
                            {catalog.seeAll.label}
                        </Link>
                    )}
                </section>
            )}

            {sections && (
                <section className={styles.sections}>
                    {sections.map((s) => (
                        <div key={s.h2} className={styles.section}>
                            <h2 className={styles.sectionHeading}>{s.h2}</h2>
                            {s.body}
                        </div>
                    ))}
                </section>
            )}

            {faq && (
                <section className={styles.faq} id="faq">
                    {faq.h2 && <h2 className={styles.h2}>{faq.h2}</h2>}
                    <div className={styles.faqList}>
                        {faq.entries.map((item, index) => (
                            <details className={styles.faqItem} key={index} name="home-faq">
                                <summary className={styles.faqItemTitle}>
                                    <span className={styles.faqQuestion}>{item.question}</span>
                                    <Icon id="add-line" className={styles.iconClosed} size={faq.iconSize ?? 20} />
                                    <Icon id="subtract-line" className={styles.iconOpen} size={faq.iconSize ?? 20} />
                                </summary>
                                <p className={styles.faqItemAnswer}>{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>
            )}

            {packIndex && <PackIndex groups={packIndex.groups} total={packIndex.total} styles={packIndex.styles} />}

            {coda}
        </div>
    )
}
